import { useState, useRef } from "react";
import {
  useDeleteDocument,
  useDocumentAuthor,
  useUpdateDocumentTags,
  useUpdateDocument,
} from "@/hooks/use-documents";
import { useDocumentDownload } from "@/hooks/use-document-download";
import { useDocumentActivities } from "@/hooks/use-document-activities";
import { useUploadNewVersion } from "@/hooks/use-upload-new-version";
import {
  useDocumentPreviousVersion,
  useDownloadPreviousVersion,
} from "@/hooks/use-document-previous-version";
import type { DocumentView } from "../types";
import type { Id } from "@/packages/types";

export function useDocumentDetail(document: DocumentView | null) {
  const [activeTab, setActiveTab] = useState<
    "details" | "suggestions" | "checklist" | "activity" | "chat"
  >("details");
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showTagEditor, setShowTagEditor] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const deleteDocument = useDeleteDocument();
  const updateDocumentTags = useUpdateDocumentTags();
  const updateDocument = useUpdateDocument();

  // Download hook — use document ID when available
  const docId = document?.id ? (document.id as Id<"documents">) : null;
  const {
    download,
    viewInNewTab,
    isLoading: isDownloadLoading,
  } = useDocumentDownload(docId);

  // Activity hook — real event data from backend
  const { activities, isLoading: isActivityLoading } =
    useDocumentActivities(docId);

  // Author hook — resolve uploadedBy user ID to name
  const { name: resolvedAuthorName, email: resolvedAuthorEmail } =
    useDocumentAuthor(document?.uploadedBy);

  // Version hooks
  const { uploadNewVersion, isUploading } = useUploadNewVersion(docId);
  const { hasPreviousVersion } = useDocumentPreviousVersion(docId);
  const { download: downloadPreviousVersion } =
    useDownloadPreviousVersion(docId);

  const handleTagEditorSave = async (tagIds: string[]) => {
    if (!docId) return;
    await updateDocumentTags({
      documentId: docId,
      tagIds: tagIds as Id<"documentTags">[],
    });
    setShowTagEditor(false);
  };

  const handleCategoryUpdate = async (categoryId: Id<"documentCategories">) => {
    if (!docId) return;
    await updateDocument({
      id: docId,
      categoryId,
    });
  };

  const handleExpirationDateUpdate = async (date: Date | undefined) => {
    if (!docId) return;
    await updateDocument({
      id: docId,
      expirationDate: date?.getTime(),
    });
  };

  const handleDelete = async () => {
    if (!document) return;
    setIsDeleting(true);
    try {
      await deleteDocument({
        id: document.id as Id<"documents">,
      });
      setShowDeleteConfirm(false);
      return true; // Signal successful deletion
    } finally {
      setIsDeleting(false);
    }
  };

  const handleNavigateToChecklistItem = (_checklistItemId: string) => {
    // Navigation to checklist item via audit is no longer supported
    // since documents are no longer directly linked to a single audit.
    // TODO: Phase 3 -- navigate via the checklist item's own audit context
    return false;
  };

  const handleUploadNewVersion = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadNewVersion(file);
    // Reset the file input so re-selecting the same file triggers onChange
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return {
    // State
    activeTab,
    setActiveTab,
    showRenameDialog,
    setShowRenameDialog,
    showDeleteConfirm,
    setShowDeleteConfirm,
    isDeleting,
    showTagEditor,
    setShowTagEditor,
    fileInputRef,

    // Data
    docId,
    download,
    viewInNewTab,
    isDownloadLoading,
    activities,
    isActivityLoading,
    resolvedAuthorName,
    resolvedAuthorEmail,
    isUploading,
    hasPreviousVersion,
    downloadPreviousVersion,

    // Handlers
    handleTagEditorSave,
    handleCategoryUpdate,
    handleExpirationDateUpdate,
    handleDelete,
    handleNavigateToChecklistItem,
    handleUploadNewVersion,
    handleFileSelected,
  };
}
